/**
 * The re-grades of a run, on the run's own page, so judge prompt versions can be compared
 * on the same outputs. The totals are the ones each re-grade saved when it ended.
 */
import { Link } from 'react-router-dom';
import { isActiveRun, type EvalRunItem } from '../../types/evalTypes';
import { formatDateTime } from '../../utils/evalFormat';
import { RunStatusBadge } from '../RunStatusBadge';

const totals = (regrade: EvalRunItem): string => {
  if (isActiveRun(regrade.status)) return `${regrade.resultCount} of ${regrade.total} graded`;
  const summary = regrade.regradeSummary;
  if (!summary) return '–';
  const flagged = summary.flaggedTotal > 0 ? `${summary.fixed} of ${summary.flaggedTotal} flagged checks fixed` : 'no flagged checks';
  return `${flagged} · ${summary.otherChanges} other ${summary.otherChanges === 1 ? 'change' : 'changes'}`;
};

export const RegradesList = ({ regrades }: { regrades: EvalRunItem[] }) => {
  if (regrades.length === 0) return null;

  return (
    <div className="bg-white rounded-lg shadow-sm p-4 space-y-2">
      <h2 className="text-lg font-semibold text-gray-900">Re-grades of this run</h2>
      <ul className="divide-y divide-gray-100">
        {regrades.map((regrade) => (
          <li key={regrade.runId} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2 text-sm">
            <Link to={`/evals/${regrade.runId}`} className="text-blue-600 hover:underline whitespace-nowrap">
              {formatDateTime(regrade.startedAt)}
            </Link>
            <RunStatusBadge run={regrade} />
            <span className="text-gray-700">{regrade.judgePrompts.map((judge) => `${judge.judgeName} v${judge.number}`).join(', ')}</span>
            <span className="text-xs text-gray-500">{regrade.regradeOf?.scope === 'flagged' ? 'flagged results' : 'all results'}</span>
            <span className="ml-auto text-gray-700">{totals(regrade)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};
